/**
 * @copyright WaterStreet. All rights reserved.
 */

import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	NgZone,
	OnDestroy,
	OnInit
} from '@angular/core';
import {
	FieldWrapper,
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	NavigationViolationService
} from '@shared/services/navigation-violation.service';
import {
	MenuItem
} from 'primeng/api';
import {
	Subject,
	takeUntil
} from 'rxjs';

@Component({
	selector: 'custom-nested-tab-content',
	templateUrl: './custom-nested-tab-content.component.html',
	styleUrls: [
		'./custom-nested-tab-content.component.scss'
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})

/**
 * A component representing an instance of a Custom Nested Tab Content Wrapper.
 * This component automatically detects when it has sibling nested tab-content
 * wrappers and renders tab navigation for the group. This is specifically for
 * nested tabs within repeaters and does not interfere with main-level tabs.
 * https://ngx-formly.github.io/ngx-formly/guide
 *
 * @export
 * @class CustomNestedTabContentComponent
 * @extends {FieldWrapper}
 * @implements {OnInit, AfterViewInit}
 */
export class CustomNestedTabContentComponent
	extends FieldWrapper
	implements OnInit, AfterViewInit, OnDestroy
{
	/**
	 * Creates an instance of CustomNestedTabContentComponent.
	 *
	 * @param {NavigationViolationService} navigationViolationService
	 * The navigation violation service.
	 * @param {ChangeDetectorRef} changeDetectorRef
	 * The change detector reference for manual change detection.
	 * @param {NgZone} ngZone
	 * The NgZone service for running operations outside
	 * Angular's zone.
	 * @memberof CustomNestedTabContentComponent
	 */
	public constructor(
		public readonly navigationViolationService:
			NavigationViolationService,
		private readonly changeDetectorRef:
			ChangeDetectorRef,
		private readonly ngZone: NgZone)
	{
		super();
	}

	/**
	 * Gets or sets whether this component should render
	 * the tab navigation. Only the first tab in a group
	 * renders the navigation.
	 *
	 * @type {boolean}
	 * @memberof CustomNestedTabContentComponent
	 */
	public shouldRenderTabNavigation: boolean = false;

	/**
	 * Gets or sets the tab items for navigation using
	 * MenuItem format compatible with the
	 * base-page-tab-menu component.
	 *
	 * @type {MenuItem[]}
	 * @memberof CustomNestedTabContentComponent
	 */
	public tabItems: MenuItem[] = [];

	/**
	 * Gets or sets the currently active tab item.
	 *
	 * @type {MenuItem}
	 * @memberof CustomNestedTabContentComponent
	 */
	public activeTabItem: MenuItem;

	/**
	 * Gets or sets the currently active section item.
	 *
	 * @type {MenuItem}
	 * @memberof CustomNestedTabContentComponent
	 */
	public activeSectionItem: MenuItem;

	/**
	 * Gets or sets the section items for single tab
	 * navigation.
	 *
	 * @type {MenuItem[]}
	 * @memberof CustomNestedTabContentComponent
	 */
	public sectionItems: MenuItem[] = [];

	/**
	 * Gets or sets the sibling tab fields for visibility
	 * management.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof CustomNestedTabContentComponent
	 */
	public siblingTabFields: FormlyFieldConfig[] = [];

	/**
	 * Cached sibling tab fields to avoid repeated
	 * filtering operations. Set once during initialization
	 * and reused throughout component lifecycle.
	 *
	 * @private
	 * @type {FormlyFieldConfig[] | null}
	 * @memberof CustomNestedTabContentComponent
	 */
	private cachedSiblingTabFields:
		FormlyFieldConfig[] | null = null;

	/**
	 * Subject used to signal component destruction and
	 * clean up subscriptions.
	 *
	 * @private
	 * @type {Subject<void>}
	 * @memberof CustomNestedTabContentComponent
	 */
	private readonly destroySubject: Subject<void> =
		new Subject<void>();

	/**
	 * Handles the component initialization lifecycle
	 * event. Ensures proper initial visibility state
	 * is set.
	 *
	 * @memberof CustomNestedTabContentComponent
	 */
	public ngOnInit(): void
	{
		if (AnyHelper.isNull(this.field.props))
		{
			this.field.props = {};
		}

		if (AnyHelper.isNull(this.field.props.visible))
		{
			this.field.props =
				{
					...this.field.props,
					visible: false
				};
		}

		this.navigationViolationService
			.violationKeysUpdated
			.pipe(takeUntil(this.destroySubject))
			.subscribe(
				() => this.changeDetectorRef
					.markForCheck());
	}

	/**
	 * Handles the after view init lifecycle event.
	 * Detects sibling nested tab content wrappers and
	 * sets up navigation.
	 *
	 * @memberof CustomNestedTabContentComponent
	 */
	public ngAfterViewInit(): void
	{
		this.initializeTabVisibility();
		this.setupTabNavigation();
	}

	/**
	 * Handles the on destroy lifecycle event.
	 * Cleans up subscriptions.
	 *
	 * @memberof CustomNestedTabContentComponent
	 */
	public ngOnDestroy(): void
	{
		this.destroySubject.next();
		this.destroySubject.complete();
	}

	/**
	 * Handles the tab selected event from the
	 * base-page-tab-menu component. Processes the event
	 * emitted by p-tabView's onChange.
	 *
	 * @param {{ index: number }} event
	 * The tab selection event from base-page-tab-menu.
	 * @memberof CustomNestedTabContentComponent
	 */
	public onTabSelectedEvent(
		event: { index: number }): void
	{
		const tabIndex: number = event?.index;

		if (AnyHelper.isNull(tabIndex)
			|| tabIndex < 0
			|| tabIndex >= this.tabItems.length)
		{
			return;
		}

		this.activeTabItem = this.tabItems[tabIndex];

		if (!AnyHelper.isNull(this.activeTabItem?.items)
			&& this.activeTabItem.items.length > 0)
		{
			this.activeSectionItem =
				this.activeTabItem.items[0];
		}
		else
		{
			this.activeSectionItem = null;
		}

		this.updateTabVisibility(tabIndex);
	}

	/**
	 * Gets all sibling fields that use the
	 * custom-nested-tab-content wrapper. Implements
	 * caching to avoid repeated filtering operations.
	 *
	 * @private
	 * @returns {FormlyFieldConfig[]}
	 * Array of sibling tab fields.
	 * @memberof CustomNestedTabContentComponent
	 */
	private getSiblingTabFields(): FormlyFieldConfig[]
	{
		if (this.cachedSiblingTabFields !== null)
		{
			return this.cachedSiblingTabFields;
		}

		if (!Array.isArray(
			this.field?.parent?.fieldGroup)
			|| AnyHelper.isNullOrEmptyArray(
				this.field.parent.fieldGroup))
		{
			this.cachedSiblingTabFields = [];

			return this.cachedSiblingTabFields;
		}

		this.cachedSiblingTabFields =
			this.field.parent.fieldGroup.filter(
				(sibling: FormlyFieldConfig) =>
					sibling.wrappers?.includes(
						'custom-nested-tab-content'));

		return this.cachedSiblingTabFields;
	}

	/**
	 * Initializes tab visibility immediately to prevent
	 * all tabs from showing on first load. This runs
	 * before setupTabNavigation to ensure proper
	 * visibility from the start.
	 *
	 * @private
	 * @memberof CustomNestedTabContentComponent
	 */
	private initializeTabVisibility(): void
	{
		const siblingTabFields: FormlyFieldConfig[] =
			this.getSiblingTabFields();

		if (siblingTabFields.length === 0)
		{
			return;
		}

		siblingTabFields.forEach(
			(field: FormlyFieldConfig, index: number) =>
			{
				if (AnyHelper.isNull(field.props))
				{
					field.props = {};
				}

				field.props = {
					...field.props,
					visible: index === 0
				};
			});

		this.changeDetectorRef.detectChanges();
	}

	/**
	 * Validates fields and determines if navigation
	 * setup should proceed. Returns true if this
	 * component should render navigation.
	 *
	 * @private
	 * @returns {boolean}
	 * True if navigation setup should continue.
	 * @memberof CustomNestedTabContentComponent
	 */
	private validateAndSetupNavigation(): boolean
	{
		const siblingTabFields: FormlyFieldConfig[] =
			this.getSiblingTabFields();

		if (siblingTabFields.length === 0)
		{
			return false;
		}

		this.siblingTabFields = siblingTabFields;

		const isFirstTab: boolean =
			siblingTabFields.indexOf(this.field) === 0;

		if (!isFirstTab)
		{
			return false;
		}

		this.shouldRenderTabNavigation = true;

		return true;
	}

	/**
	 * Configures navigation for single tab scenario.
	 * Sets up section-only navigation without tab
	 * headers.
	 *
	 * @private
	 * @param {FormlyFieldConfig} tabField
	 * The single tab field to configure.
	 * @memberof CustomNestedTabContentComponent
	 */
	private setupSingleTabNavigation(
		tabField: FormlyFieldConfig): void
	{
		this.tabItems = [];
		this.sectionItems =
			this.createSectionItemsForTab(tabField);

		if (this.sectionItems.length > 0)
		{
			this.activeSectionItem =
				this.sectionItems[0];
		}
	}

	/**
	 * Configures navigation for multiple tabs scenario.
	 * Sets up both tab headers and section navigation.
	 *
	 * @private
	 * @param {FormlyFieldConfig[]} tabFields
	 * The tab fields to configure.
	 * @memberof CustomNestedTabContentComponent
	 */
	private setupMultiTabNavigation(
		tabFields: FormlyFieldConfig[]): void
	{
		this.sectionItems = [];
		this.tabItems =
			tabFields.map(
				(tab: FormlyFieldConfig, index: number) =>
					({
						label: tab.props?.label
							|| `Tab ${index + 1}`,
						items: this.createSectionItemsForTab(
							tab)
					}));

		if (this.tabItems.length > 0)
		{
			this.activeTabItem = this.tabItems[0];

			if (this.activeTabItem.items
				&& this.activeTabItem.items.length > 0)
			{
				this.activeSectionItem =
					this.activeTabItem.items[0];
			}
		}
	}

	/**
	 * Finalizes component state after navigation setup.
	 * Handles change detection and ensures proper active
	 * state.
	 *
	 * @private
	 * @memberof CustomNestedTabContentComponent
	 */
	private finalizeTabState(): void
	{
		if (this.tabItems.length > 0
			&& !this.activeTabItem)
		{
			this.activeTabItem = this.tabItems[0];
		}

		this.changeDetectorRef.detectChanges();
	}

	/**
	 * Sets up tab navigation by orchestrating the setup
	 * process. Delegates to specialized methods for each
	 * type of setup.
	 *
	 * @private
	 * @memberof CustomNestedTabContentComponent
	 */
	private setupTabNavigation(): void
	{
		if (!this.validateAndSetupNavigation())
		{
			return;
		}

		if (this.siblingTabFields.length === 1)
		{
			this.setupSingleTabNavigation(
				this.siblingTabFields[0]);
		}
		else
		{
			this.setupMultiTabNavigation(
				this.siblingTabFields);
		}

		this.finalizeTabState();
	}

	/**
	 * Updates the visibility of all tab content based
	 * on the active tab.
	 *
	 * @private
	 * @param {number} activeTabIndex
	 * The index of the currently active tab.
	 * @memberof CustomNestedTabContentComponent
	 */
	private updateTabVisibility(
		activeTabIndex: number): void
	{
		this.siblingTabFields.forEach(
			(tabField: FormlyFieldConfig,
				index: number) =>
			{
				const isVisible: boolean =
					index === activeTabIndex;
				tabField.props = {
					...tabField.props,
					visible: isVisible
				};
			});

		const currentTabItems: MenuItem[] =
			this.tabItems;
		this.tabItems = currentTabItems.map(
			(item: MenuItem) =>
				({ label: item.label }));
		this.changeDetectorRef.detectChanges();

		this.ngZone.run(
			() =>
			{
				this.tabItems = [...currentTabItems];
				this.activeTabItem =
					this.tabItems[activeTabIndex];
				this.changeDetectorRef.detectChanges();
			});
	}

	/**
	 * Creates section menu items for a tab based on
	 * custom-section-title fields within the tab's
	 * fieldGroup.
	 *
	 * @private
	 * @param {FormlyFieldConfig} tabField
	 * The tab field to scan for section titles.
	 * @returns {MenuItem[]}
	 * Array of section menu items for the tab.
	 * @memberof CustomNestedTabContentComponent
	 */
	private createSectionItemsForTab(
		tabField: FormlyFieldConfig): MenuItem[]
	{
		if (!Array.isArray(tabField.fieldGroup)
			|| AnyHelper.isNullOrEmptyArray(
				tabField.fieldGroup))
		{
			return [];
		}

		const sectionTitleFields =
			tabField.fieldGroup.filter(
				(field: FormlyFieldConfig) =>
					field.type === 'custom-section-title'
						&& field.props?.label);

		return sectionTitleFields.map(
			(sectionField: FormlyFieldConfig) =>
			{
				const sectionId: string =
					this.calculateSectionId(
						sectionField.props.label);

				const sectionItem: MenuItem = {
					label: sectionField.props.label,
					command: () =>
					{
						this.activeSectionItem =
							sectionItem;
						this.changeDetectorRef
							.detectChanges();
						this.scrollToSection(sectionId);
					}
				};

				return sectionItem;
			});
	}

	/**
	 * Calculates the section ID that will be generated
	 * by the custom-section-title component. Uses
	 * StringHelper.getCleanedValue to ensure consistency
	 * with CustomSectionTitleComponent.
	 *
	 * @private
	 * @param {string} label
	 * The section label.
	 * @returns {string}
	 * The section ID that will be assigned by the
	 * custom-section-title component.
	 * @memberof CustomNestedTabContentComponent
	 */
	private calculateSectionId(
		label: string): string
	{
		const cleanedValue: string =
			StringHelper.getCleanedValue(label);

		return cleanedValue + 'Section';
	}

	/**
	 * Scrolls to a section element with the given ID.
	 * Runs outside Angular's zone for better
	 * performance.
	 *
	 * @private
	 * @param {string} sectionId
	 * The ID of the section to scroll to.
	 * @memberof CustomNestedTabContentComponent
	 */
	private scrollToSection(
		sectionId: string): void
	{
		this.ngZone.runOutsideAngular(
			() =>
			{
				requestAnimationFrame(
					() =>
					{
						const element: HTMLElement =
							document.getElementById(
								sectionId);

						if (element)
						{
							element.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
								inline: 'nearest'
							});
						}
					});
			});
	}
}